/**
 * Observes edits to the real Word paragraph at the cursor (track changes on).
 * Disabled by default — set localStorage `courtyard:enableWordEditCapture=1` to enable.
 */

import { logError } from '../../utils/logging';
import {
  buildParagraphEditSnapshot,
  inferWordEditSemantics,
  isWordEditCaptureEnabled,
  logAcceptedTextDelta,
  logParagraphSnapshot,
  logWordEditCapture,
  type ParagraphEditSnapshot,
} from '../../utils/wordEditCaptureDebug';
/* global Word, Office */

const POLL_MS = 280;

function isWordHost(): boolean {
  return typeof Word !== 'undefined' && typeof Word.run === 'function';
}

function isSelectionHandlerAvailable(): boolean {
  return (
    typeof Office !== 'undefined' &&
    !!Office.context?.document &&
    typeof Office.context.document.addHandlerAsync === 'function' &&
    !!Office.EventType?.DocumentSelectionChanged
  );
}

export class WordParagraphEditCapture {
  private static instance: WordParagraphEditCapture | null = null;

  private running = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private seq = 0;
  private lastSnapshot: ParagraphEditSnapshot | null = null;
  private lastAcceptedFingerprint = '';
  private pollInFlight = false;
  private selectionHandlerBound: (() => void) | null = null;

  static getInstance(): WordParagraphEditCapture {
    if (!WordParagraphEditCapture.instance) {
      WordParagraphEditCapture.instance = new WordParagraphEditCapture();
    }
    return WordParagraphEditCapture.instance;
  }

  start(): void {
    if (this.running || !isWordHost() || !isWordEditCaptureEnabled()) {
      return;
    }
    this.running = true;
    this.seq = 0;
    this.lastSnapshot = null;
    this.lastAcceptedFingerprint = '';

    logWordEditCapture('WORD_PARAGRAPH — capture started', {
      pollMs: POLL_MS,
      hint: 'Set localStorage courtyard:disableWordEditCapture=1 to stop',
    });

    this.setupSelectionHandler();
    void this.captureOnce('startup');
    this.pollTimer = setInterval(() => {
      void this.captureOnce('poll');
    }, POLL_MS);
  }

  stop(): void {
    this.running = false;
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    logWordEditCapture('WORD_PARAGRAPH — capture stopped', { lastSeq: this.seq });
  }

  private setupSelectionHandler(): void {
    if (!isSelectionHandlerAvailable() || this.selectionHandlerBound) return;

    this.selectionHandlerBound = () => {
      void this.captureOnce('selection-changed');
    };

    Office.context.document.addHandlerAsync(
      Office.EventType.DocumentSelectionChanged,
      this.selectionHandlerBound,
      (result: Office.AsyncResult<void>) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          logError('[WordEditCapture] selection handler setup failed', result.error);
        } else {
          logWordEditCapture('WORD_PARAGRAPH — selection handler ready', {});
        }
      },
    );
  }

  private async captureOnce(trigger: string): Promise<void> {
    if (!this.running || this.pollInFlight) return;
    this.pollInFlight = true;

    try {
      const raw = await this.readParagraphAtCursor();
      if (!raw) return;

      this.seq += 1;
      const snapshot = buildParagraphEditSnapshot({
        source: 'word-paragraph',
        seq: this.seq,
        ooxml: raw.ooxml,
        selectionText: raw.selectionText,
        paragraphIndex: raw.paragraphIndex,
        changeTrackingMode: raw.changeTrackingMode,
      });

      const fingerprint = `${snapshot.acceptedText.length}:${snapshot.acceptedText}`;

      if (trigger === 'startup' || trigger === 'selection-changed') {
        logWordEditCapture('WORD_PARAGRAPH — observe', {
          trigger,
          seq: snapshot.seq,
          paragraphIndex: snapshot.paragraphIndex,
          selectionLen: snapshot.selectionLen,
          acceptedPreview: snapshot.acceptedSnapshot.preview,
          trackChangesOn: raw.changeTrackingMode,
        });
      }

      if (fingerprint !== this.lastAcceptedFingerprint) {
        const prev = this.lastSnapshot;
        logParagraphSnapshot('WORD_PARAGRAPH — snapshot', snapshot);

        if (prev) {
          logAcceptedTextDelta('WORD_PARAGRAPH — accepted delta', prev.acceptedText, snapshot.acceptedText, {
            trigger,
            seq: snapshot.seq,
            prevSeq: prev.seq,
            paragraphIndex: snapshot.paragraphIndex,
          });

          if (
            prev.ooxmlLen !== snapshot.ooxmlLen ||
            prev.trackChangeSegments.length !== snapshot.trackChangeSegments.length ||
            JSON.stringify(prev.trackChangeSegments) !== JSON.stringify(snapshot.trackChangeSegments)
          ) {
            const semantics = inferWordEditSemantics(
              prev.trackChangeSegments,
              snapshot.trackChangeSegments,
            );
            logWordEditCapture('WORD_PARAGRAPH — ooxml structure change', {
              trigger,
              seq: snapshot.seq,
              ooxmlLenBefore: prev.ooxmlLen,
              ooxmlLenAfter: snapshot.ooxmlLen,
              segmentsBefore: prev.trackChangeSegments.length,
              segmentsAfter: snapshot.trackChangeSegments.length,
              segmentsBeforeDetail: prev.trackChangeSegments,
              segmentsAfterDetail: snapshot.trackChangeSegments,
              wordEditSemantics: semantics.length ? semantics : ['structure reorder or same block count'],
            });
          }
        } else {
          logWordEditCapture('WORD_PARAGRAPH — baseline established', {
            trigger,
            seq: snapshot.seq,
            acceptedLen: snapshot.acceptedText.length,
          });
        }

        this.lastAcceptedFingerprint = fingerprint;
        this.lastSnapshot = snapshot;
      }
    } catch (err) {
      logError('[WordEditCapture] capture failed', err);
    } finally {
      this.pollInFlight = false;
    }
  }

  private async readParagraphAtCursor(): Promise<{
    ooxml: string;
    selectionText: string;
    paragraphIndex: number;
    changeTrackingMode: string;
  } | null> {
    return Word.run(async (context) => {
      const doc = context.document;
      doc.load('changeTrackingMode');
      const selection = context.document.getSelection();
      selection.load('text');

      const paragraph = selection.paragraphs.getFirst();
      paragraph.load('text');

      await context.sync();

      const paragraphIndex = -1;

      let ooxml = '';
      try {
        const ooxmlResult = (paragraph as any).getOoxml();
        await context.sync();
        ooxml = ooxmlResult?.value ?? '';
      } catch {
        ooxml = (paragraph as any).ooxml || '';
      }

      const changeTrackingMode = String((doc as any).changeTrackingMode ?? 'unknown');

      return {
        ooxml,
        selectionText: selection.text || '',
        paragraphIndex,
        changeTrackingMode,
      };
    });
  }
}

/** Start global Word paragraph edit capture (no-op outside Word / when disabled). */
export function startWordParagraphEditCapture(): void {
  if (!isWordHost() || !isWordEditCaptureEnabled()) return;
  WordParagraphEditCapture.getInstance().start();
}
